import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
    countriesAtom,
    selectedCountryAtom,
    isLoadingCountriesAtom,
    ushopCountriesAtom,
    expressCountriesAtom,
    feelgreatCountriesAtom,
    setCountriesAtom,
    setLoadingAtom,
    selectCountryAtom
} from '../stores/countries.store'
import { countriesApi } from '../services/country.apis'
import type { TCountry } from '../types'
import { useEffect } from 'react'
import { RetryGuard } from '../../guard'

// Guard rail: on a failed getCountries the loading flag flips back to false while
// countries stay empty, so callers (e.g. an effect gated on `!countries?.data`)
// re-trigger fetchCountries forever. Cap it at 3 consecutive failures — once
// exhausted, fetchCountries no-ops so the loading flag never toggles again and the
// caller's effect stops re-firing. Module-level: the cap is shared across mounts.
const countriesRetryGuard = new RetryGuard(3)

// Hook สำหรับจัดการ countries ทั้งหมด
export const useCountries = () => {
    const countries = useAtomValue(countriesAtom)
    const isLoading = useAtomValue(isLoadingCountriesAtom)
    const setCountriesAction = useSetAtom(setCountriesAtom)
    const setLoadingAction = useSetAtom(setLoadingAtom)

    const fetchCountries = async () => {
        // Stop re-triggering once the endpoint has failed too many times in a row.
        if (countriesRetryGuard.exhausted) return
        setLoadingAction(true)
    }

    const getCountries = async () => {
        try {
            setLoadingAction(true)
            const response = await countriesApi.getCountries()
            setCountriesAction(response.data)
            countriesRetryGuard.succeed()
            setTimeout(() => {
                setLoadingAction(false)
            }, 1000)
        } catch (error) {
            console.error('Failed to fetch countries:', error)
            countriesRetryGuard.fail()
            setLoadingAction(false)
        }
    }

    useEffect(() => {
        if (isLoading) {
            getCountries()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading])

    return {
        countries,
        isLoading,
        fetchCountries,
        refetch: fetchCountries
    }
}

// Hook สำหรับ countries แยกตาม type
export const useCountriesByType = () => {
    const ushopCountries = useAtomValue(ushopCountriesAtom)
    const expressCountries = useAtomValue(expressCountriesAtom)
    const feelgreatCountries = useAtomValue(feelgreatCountriesAtom)

    return {
        ushopCountries,
        expressCountries,
        feelgreatCountries
    }
}

// Hook สำหรับ selected country
export const useSelectedCountry = () => {
    const [selectedCountry, setSelectedCountry] = useAtom(selectedCountryAtom)
    const selectCountryAction = useSetAtom(selectCountryAtom)

    const selectCountry = (country: TCountry) => {
        selectCountryAction(country)
    }

    const clearSelection = () => {
        setSelectedCountry(null)
    }

    return {
        selectedCountry,
        selectCountry,
        clearSelection
    }
}

// Hook แบบ read-only สำหรับ performance
export const useCountriesValue = () => {
    return useAtomValue(countriesAtom)
}

export const useIsLoadingCountries = () => {
    return useAtomValue(isLoadingCountriesAtom)
}
